import { jsPDF } from 'jspdf';

import type { RgbTuple } from './exportBackground';

/** CSS pixels per millimetre at the 96 dpi a browser lays a page out at. */
const PX_PER_MM = 96 / 25.4;

/** The same A4 portrait page with 5 mm margins react-to-pdf's `Margin.SMALL` gave the reports. */
export const PDF_PAGE = { format: 'a4', orientation: 'portrait', marginMm: 5 } as const;

/** Full JPEG quality: the slices are screenshots of text, which lossy settings smear. */
const JPEG_QUALITY = 1;

export interface CanvasToPdfOptions {
    /** The captured element's CSS width, which fixes how wide the image sits on the page. */
    contentWidthPx: number;
    /** Painted over the whole page before the image, so margins and the tail match it. */
    background: RgbTuple | null;
    /** Stretches of the capture, in CSS pixels from its top, that a page break should not cut. */
    keepTogether?: readonly PdfRange[];
}

/** A vertical stretch, top inclusive, bottom exclusive. */
export interface PdfRange {
    top: number;
    bottom: number;
}

/**
 * Marks what a page break should not cut. `self` keeps the element whole; `children` keeps each
 * of its children whole, as for the cards of a grid, which share a row and so a top.
 */
export const PDF_KEEP_TOGETHER_ATTR = 'data-pdf-keep-together';

export type PdfKeepTogether = 'self' | 'children';

/** Where the marked blocks inside `element` sit, in CSS pixels from its top. */
export function measureKeepTogether(element: HTMLElement): PdfRange[] {
    const origin = element.getBoundingClientRect().top;
    const marked = Array.from(element.querySelectorAll<HTMLElement>(`[${PDF_KEEP_TOGETHER_ATTR}]`));
    return marked.flatMap((node) => {
        const blocks =
            node.getAttribute(PDF_KEEP_TOGETHER_ATTR) === 'children'
                ? Array.from(node.children)
                : [node];
        return blocks.map((block) => {
            const rect = block.getBoundingClientRect();
            return { top: rect.top - origin, bottom: rect.bottom - origin };
        });
    });
}

/** The part of a jsPDF document the layout draws with, so tests can record the calls. */
export interface PdfSink {
    internal: { pageSize: { getWidth(): number; getHeight(): number } };
    addPage(format?: string, orientation?: 'portrait'): unknown;
    setFillColor(r: number, g: number, b: number): unknown;
    rect(x: number, y: number, width: number, height: number, style: 'F'): unknown;
    addImage(
        imageData: string,
        format: string,
        x: number,
        y: number,
        width: number,
        height: number
    ): unknown;
}

export interface PdfSlice {
    /** Top of the slice in canvas pixels. */
    sourceY: number;
    /** Height of the slice in canvas pixels. */
    sourceHeight: number;
}

/**
 * How a capture of `canvasHeight` canvas pixels splits into pages that each hold
 * `pageHeight` canvas pixels: full pages, then whatever is left on the last one.
 *
 * A break that would cut one of `keepTogether` (canvas pixels) moves up to its top, so the block
 * starts the next page and this one ends short. A block that starts the page, or is taller than
 * a page, is cut where the page ends: no page could hold it whole.
 */
export function slicePages(
    canvasHeight: number,
    pageHeight: number,
    keepTogether: readonly PdfRange[] = []
): PdfSlice[] {
    if (canvasHeight <= 0 || pageHeight <= 0) return [];
    const blocks = keepTogether.filter((block) => block.bottom - block.top <= pageHeight);
    const slices: PdfSlice[] = [];
    let sourceY = 0;
    while (sourceY < canvasHeight) {
        let end = Math.min(sourceY + pageHeight, canvasHeight);
        // Moving the break up can land it inside another block; repeat until none is cut.
        for (;;) {
            const breakAt = end;
            const cut = blocks.filter(
                (block) => block.top > sourceY && block.top < breakAt && block.bottom > breakAt
            );
            if (end >= canvasHeight || cut.length === 0) break;
            end = Math.min(...cut.map((block) => block.top));
        }
        slices.push({ sourceY, sourceHeight: end - sourceY });
        sourceY = end;
    }
    return slices;
}

function sliceToJpeg(canvas: HTMLCanvasElement, slice: PdfSlice): string {
    const page = document.createElement('canvas');
    page.width = canvas.width;
    page.height = slice.sourceHeight;
    const context = page.getContext('2d');
    if (!context) throw new Error('Canvas 2D context is unavailable');
    context.drawImage(
        canvas,
        0,
        slice.sourceY,
        canvas.width,
        slice.sourceHeight,
        0,
        0,
        canvas.width,
        slice.sourceHeight
    );
    return page.toDataURL('image/jpeg', JPEG_QUALITY);
}

/**
 * Lays a captured canvas out on `pdf`, one page per slice: scaled down to the printable width
 * when wider, placed inside the margins, and continued on a new page at the bottom margin.
 *
 * jsPDF paper is white, so a dark export would sit in a white frame with a white block under
 * its last line. Each page is filled with `background` first, under the image. A white
 * background is skipped, which leaves a light export exactly as the paper is.
 */
export function layoutCanvasOnPdf(
    pdf: PdfSink,
    canvas: HTMLCanvasElement,
    options: CanvasToPdfOptions,
    toImage: (canvas: HTMLCanvasElement, slice: PdfSlice) => string = sliceToJpeg
): void {
    const { contentWidthPx, background, keepTogether = [] } = options;
    const margin = PDF_PAGE.marginMm;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const printableWidth = pageWidth - 2 * margin;
    const printableHeight = pageHeight - 2 * margin;

    const imageWidth = Math.min(printableWidth, contentWidthPx / PX_PER_MM);
    const mmPerCanvasPx = imageWidth / canvas.width;
    const pageHeightPx = Math.floor(printableHeight / mmPerCanvasPx);
    const fill = background && !background.every((channel) => channel === 255);

    // html-to-image may scale a tall capture down, so canvas pixels per CSS pixel are measured.
    const canvasPxPerCssPx = canvas.width / contentWidthPx;
    const blocks = keepTogether.map((block) => ({
        top: Math.floor(block.top * canvasPxPerCssPx),
        bottom: Math.ceil(block.bottom * canvasPxPerCssPx),
    }));

    slicePages(canvas.height, pageHeightPx, blocks).forEach((slice, index) => {
        if (index > 0) pdf.addPage(PDF_PAGE.format, PDF_PAGE.orientation);
        if (fill) {
            pdf.setFillColor(background[0], background[1], background[2]);
            pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        }
        pdf.addImage(
            toImage(canvas, slice),
            'JPEG',
            margin,
            margin,
            imageWidth,
            slice.sourceHeight * mmPerCanvasPx
        );
    });
}

/** A new A4 document holding `canvas`, laid out by {@link layoutCanvasOnPdf}. */
export function canvasToPdf(canvas: HTMLCanvasElement, options: CanvasToPdfOptions): jsPDF {
    const pdf = new jsPDF({
        format: PDF_PAGE.format,
        orientation: PDF_PAGE.orientation,
        unit: 'mm',
    });
    layoutCanvasOnPdf(pdf, canvas, options);
    return pdf;
}
